import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, Info, Loader2, Play } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAdminVerification, type VerificationResult, type VerificationIssue } from './AdminVerificationContext';

export default function AdminDataVerification() {
    const {
        bibleResult: result,
        setBibleResult: setResult,
        isVerifyingBible: isVerifying,
        setIsVerifyingBible: setIsVerifying
    } = useAdminVerification();

    const handleVerify = async () => {
        if (!confirm('Run Bible data verification? This will check sections coverage and paragraph data integrity.')) {
            return;
        }

        setIsVerifying(true);
        setResult(null);

        const functions = getFunctions();
        const verifyFunction = httpsCallable(functions, 'verifyBibleDataFunction', {
            timeout: 300000 // 5 minutes
        });

        try {
            console.log('Starting Bible data verification...');
            const response = await verifyFunction({}) as any;
            console.log('Verification complete:', response);

            setResult(response.data as VerificationResult);
        } catch (error: any) {
            console.error('Verification failed:', error);
            setResult({
                success: false,
                timestamp: new Date().toISOString(),
                issues: [{
                    type: 'error',
                    category: 'system-error',
                    message: error.message || 'Unknown error occurred'
                }],
                stats: {
                    totalSections: 0,
                    booksWithSections: 0,
                    totalBooks: 66,
                    chaptersVerified: 0,
                    totalChapters: 1189,
                    versesVerified: 0,
                    totalVerses: 0
                }
            });
        } finally {
            setIsVerifying(false);
        }
    };


    const getIssueIcon = (type: string) => {
        switch (type) {
            case 'error':
                return <XCircle className="h-4 w-4 text-red-500" />;
            case 'warning':
                return <AlertTriangle className="h-4 w-4 text-orange-500" />;
            case 'info':
                return <Info className="h-4 w-4 text-blue-500" />;
            default:
                return null;
        }
    };

    const groupIssuesByCategory = (issues: VerificationIssue[]) => {
        const grouped = new Map<string, VerificationIssue[]>();
        for (const issue of issues) {
            if (!grouped.has(issue.category)) {
                grouped.set(issue.category, []);
            }
            grouped.get(issue.category)!.push(issue);
        }
        return grouped;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Bible Data Verification
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                    Verify the integrity of Bible data:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>All books and chapters are covered by sections</li>
                        <li>All verses are covered by sections (verse-level check)</li>
                        <li>No overlapping sections within books</li>
                        <li>All paragraphs have passage IDs assigned</li>
                        <li>Section references are valid</li>
                        <li>Genre/subgenre/form values are valid according to genre taxonomy</li>
                    </ul>
                </div>

                <Button
                    onClick={handleVerify}
                    disabled={isVerifying}
                    className="w-full"
                >
                    {isVerifying ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying...
                        </>
                    ) : (
                        <>
                            <Play className="mr-2 h-4 w-4" />
                            Run Verification
                        </>
                    )}
                </Button>

                {result && (
                    <div className="space-y-4 mt-6">
                        {/* Summary Stats */}
                        <div className="p-4 bg-muted rounded-lg space-y-2">
                            <div className="font-semibold">Verification Summary</div>
                            <div className="text-sm space-y-1">
                                <div>Timestamp: {new Date(result.timestamp).toLocaleString()}</div>
                                <div>Total Sections: {result.stats.totalSections}</div>
                                <div>Books with Sections: {result.stats.booksWithSections} / {result.stats.totalBooks}</div>
                                <div>Chapters Verified: {result.stats.chaptersVerified} / {result.stats.totalChapters}</div>
                                {result.stats.versesVerified !== undefined && result.stats.totalVerses !== undefined && (
                                    <div>Verses Verified: {result.stats.versesVerified.toLocaleString()} / {result.stats.totalVerses.toLocaleString()}</div>
                                )}
                                {result.stats.paragraphsVerified !== undefined && (
                                    <div>Paragraphs Verified: {result.stats.paragraphsVerified.toLocaleString()}</div>
                                )}
                                {result.stats.versionsVerified && result.stats.versionsVerified.length > 0 && (
                                    <div>Versions Checked: {result.stats.versionsVerified.join(', ').toUpperCase()}</div>
                                )}
                            </div>
                        </div>

                        {/* Issues Summary */}
                        <div className="flex gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span>{result.issues.filter(i => i.type === 'error').length} Errors</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-orange-500" />
                                <span>{result.issues.filter(i => i.type === 'warning').length} Warnings</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Info className="h-4 w-4 text-blue-500" />
                                <span>{result.issues.filter(i => i.type === 'info').length} Info</span>
                            </div>
                        </div>

                        {/* Issues List */}
                        {result.issues.length > 0 ? (
                            <div className="space-y-4">
                                <div className="font-semibold">Issues Found:</div>
                                {Array.from(groupIssuesByCategory(result.issues)).map(([category, issues]) => (
                                    <div key={category} className="border rounded-lg p-3 space-y-2">
                                        <div className="font-medium capitalize">
                                            {category.replace(/-/g, ' ')} ({issues.length})
                                        </div>
                                        <div className="space-y-1">
                                            {/* Special handling for genre-validation-summary */}
                                            {category === 'genre-validation-summary' ? (
                                                issues.map((issue, idx) => (
                                                    <div key={idx} className="flex items-start gap-2 text-sm">
                                                        {getIssueIcon(issue.type)}
                                                        <div className="flex-1">
                                                            <div className="font-medium mb-2">{issue.message}</div>
                                                            {issue.details && (
                                                                <div className="space-y-3">
                                                                    {issue.details.invalidGenres && issue.details.invalidGenres.length > 0 && (
                                                                        <div>
                                                                            <div className="text-xs font-semibold text-red-600 mb-1">Invalid Genres:</div>
                                                                            <div className="text-xs space-y-1">
                                                                                {issue.details.invalidGenres.map((item: any, i: number) => (
                                                                                    <div key={i} className="font-mono">
                                                                                        {item.combination} ({item.count} passage{item.count > 1 ? 's' : ''})
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {issue.details.invalidSubgenres && issue.details.invalidSubgenres.length > 0 && (
                                                                        <div>
                                                                            <div className="text-xs font-semibold text-orange-600 mb-1">Invalid Subgenres:</div>
                                                                            <div className="text-xs space-y-1">
                                                                                {issue.details.invalidSubgenres.map((item: any, i: number) => (
                                                                                    <div key={i} className="font-mono">
                                                                                        {item.combination} ({item.count} passage{item.count > 1 ? 's' : ''})
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {issue.details.invalidForms && issue.details.invalidForms.length > 0 && (
                                                                        <div>
                                                                            <div className="text-xs font-semibold text-yellow-600 mb-1">Invalid Forms:</div>
                                                                            <div className="text-xs space-y-1">
                                                                                {issue.details.invalidForms.map((item: any, i: number) => (
                                                                                    <div key={i} className="font-mono">
                                                                                        {item.combination} ({item.count} passage{item.count > 1 ? 's' : ''})
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <>
                                                    {issues.slice(0, 10).map((issue, idx) => (
                                                        <div key={idx} className="flex items-start gap-2 text-sm">
                                                            {getIssueIcon(issue.type)}
                                                            <div className="flex-1">
                                                                <div>{issue.message}</div>
                                                                {issue.details && (
                                                                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                                                                        {JSON.stringify(issue.details, null, 2)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {issues.length > 10 && (
                                                        <div className="text-xs text-muted-foreground">
                                                            ... and {issues.length - 10} more
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-lg">
                                <CheckCircle className="h-5 w-5" />
                                <span className="font-medium">All checks passed! No issues found.</span>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

